import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import InstalledEquipmentAssetApi from "../../../../../infrastructure/api/installedEquipmentAssets";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EquipmentItemPicker from "../../shared/equipmentCatalog/EquipmentItemPicker";
import EquipmentPublicCodeChip from "../../shared/equipmentCatalog/EquipmentPublicCodeChip";

const EMPTY_FORM = {
  id: 0,
  requestId: 0,
  equipmentItemId: null,
  publicCode: "",
  serialNumber: "",
  quantity: 1,
  installedAt: "",
  warrantyStartDate: "",
  warrantyEndDate: "",
  notes: "",
  masterPicker: null,
};

const InstalledEquipmentPage = () => {
  const [searchParams] = useSearchParams();
  const [requestId, setRequestId] = useState(searchParams.get("requestId") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, form: EMPTY_FORM });

  const load = useCallback(async () => {
    const rid = Number(requestId);
    if (!rid) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await InstalledEquipmentAssetApi.getByRequest(rid);
      setRows(res?.data || []);
    } catch {
      toast.error("خطا در بارگذاری تجهیزات نصب‌شده");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    const rid = Number(requestId);
    if (!rid) return toast.warn("شناسه درخواست را وارد کنید.");
    setDialog({
      open: true,
      form: { ...EMPTY_FORM, requestId: rid },
    });
  };

  const save = async () => {
    const f = dialog.form;
    const res = await InstalledEquipmentAssetApi.saveAsset({
      id: f.id || 0,
      requestId: f.requestId,
      equipmentItemId: f.equipmentItemId,
      serialNumber: f.serialNumber || null,
      quantity: Number(f.quantity) || 1,
      installedAt: f.installedAt || null,
      warrantyStartDate: f.warrantyStartDate || null,
      warrantyEndDate: f.warrantyEndDate || null,
      notes: f.notes || null,
    });
    if (res?.isSuccess !== false) {
      toast.success("تجهیز نصب‌شده ثبت شد");
      setDialog({ open: false, form: EMPTY_FORM });
      load();
    } else toast.error(res?.message || "خطا در ثبت");
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="ثبت تجهیزات نصب‌شده و سریال"
            action={
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="شناسه درخواست"
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                />
                <Button variant="outlined" onClick={load} disabled={loading}>
                  بارگذاری
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  ثبت تجهیز
                </Button>
              </Stack>
            }
          />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              ثبت سریال Logger در فرم اتصال به شبکه (GridConnection) همچنان مستقل باقی می‌ماند.
              این صفحه برای ثبت دارایی‌های تجهیزات نصب‌شده با کد مرجع EQM است.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>کد</TableCell>
                  <TableCell>سریال</TableCell>
                  <TableCell>تعداد</TableCell>
                  <TableCell>وضعیت</TableCell>
                  <TableCell>تاریخ نصب</TableCell>
                  <TableCell>گارانتی تا</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <EquipmentPublicCodeChip code={r.publicCode} />
                    </TableCell>
                    <TableCell>{r.serialNumber || "—"}</TableCell>
                    <TableCell>{r.quantity}</TableCell>
                    <TableCell>{r.statusTitle || r.status}</TableCell>
                    <TableCell>{r.installedAt ? String(r.installedAt).slice(0, 10) : "—"}</TableCell>
                    <TableCell>
                      {r.warrantyEndDate ? String(r.warrantyEndDate).slice(0, 10) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary">موردی یافت نشد.</Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, form: EMPTY_FORM })} fullWidth maxWidth="sm">
        <DialogTitle>ثبت تجهیز نصب‌شده</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <EquipmentItemPicker
              value={dialog.form.masterPicker}
              onChange={(picked) =>
                setDialog((d) => ({
                  ...d,
                  form: {
                    ...d.form,
                    masterPicker: picked,
                    equipmentItemId: picked?.equipmentItemId || null,
                    publicCode: picked?.publicCode || "",
                  },
                }))
              }
            />
            {dialog.form.publicCode ? (
              <EquipmentPublicCodeChip code={dialog.form.publicCode} />
            ) : null}
            <TextField
              label="شماره سریال"
              value={dialog.form.serialNumber}
              onChange={(e) =>
                setDialog((d) => ({ ...d, form: { ...d.form, serialNumber: e.target.value } }))
              }
            />
            <TextField
              label="تعداد"
              type="number"
              value={dialog.form.quantity}
              onChange={(e) =>
                setDialog((d) => ({ ...d, form: { ...d.form, quantity: e.target.value } }))
              }
            />
            <TextField
              label="تاریخ نصب"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dialog.form.installedAt}
              onChange={(e) =>
                setDialog((d) => ({ ...d, form: { ...d.form, installedAt: e.target.value } }))
              }
            />
            <TextField
              label="شروع گارانتی"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dialog.form.warrantyStartDate}
              onChange={(e) =>
                setDialog((d) => ({
                  ...d,
                  form: { ...d.form, warrantyStartDate: e.target.value },
                }))
              }
            />
            <TextField
              label="پایان گارانتی"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dialog.form.warrantyEndDate}
              onChange={(e) =>
                setDialog((d) => ({
                  ...d,
                  form: { ...d.form, warrantyEndDate: e.target.value },
                }))
              }
            />
            <TextField
              label="یادداشت"
              multiline
              minRows={2}
              value={dialog.form.notes}
              onChange={(e) =>
                setDialog((d) => ({ ...d, form: { ...d.form, notes: e.target.value } }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, form: EMPTY_FORM })}>انصراف</Button>
          <Button variant="contained" onClick={save}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default InstalledEquipmentPage;
